import React, { useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import GridContext from "../../components/shared/grid-bits/context/GridContext";
import useDetailedView from "../../custom-hooks/detailed-view/useDetailedView";
import { mapResourceTypeToColumns } from "../../components/shared/grid-bits/mapResourceTypeToColumns";
import { ProvisionedAppType, CustomizedRowProps } from "./types";
import ResourceIcon from "../../components/shared/grid-bits/resources/ResourceIcon";
import OrchestrationStackQuickView from "../../components/shared/quickview/extensions/OrchestrationStackQuickView";
import QuickView from "../../components/shared/quickview/QuickView";
import ProvisionedAppMenu from "./bits/ProvisionedAppMenu";
import MoreData from "./bits/MoreData";
import { GridColumn } from "../../components/shared/grid-bits/types";
import { State } from "../../selectors/state";
import SelectResource from "../../components/shared/grid-bits/select/SelectResource";

const CustomizedRow: React.FC<CustomizedRowProps> = ({ resource }) => {
  const { resourceType } = useContext(GridContext);
  const { isDetailedViewOn, toggleDetailedView, detailedClassNames } =
    useDetailedView({
      resource,
      resourceType,
    });
  const columns = mapResourceTypeToColumns[resourceType];

  const orchestrationStacks = useSelector(
    (state: State) => state.orchestration_stacks?.ORCHESTRATION_STACKS_LIST,
  );
  const isHeatDeployment =
    resource?.deploymentTypeTitle.toLowerCase() === "heat";

  const orchestrationStack = useMemo(() => {
    if (isHeatDeployment && resource?.resourceId && orchestrationStacks) {
      return orchestrationStacks[resource.resourceId as string];
    }
    return null;
  }, [isHeatDeployment, resource?.resourceId, orchestrationStacks]);
  const paddingJustificationForIcon = (column: GridColumn) =>
    column.name === "status" ? "padding-left" : "";

  if (!resource) return null;

  const Item: React.FC<{ item: string }> = ({ item }) => {
    switch (item) {
      case "checkbox":
        return <SelectResource id={resource.id} resourceType={resourceType} />;
      case "hamburger_menu":
        return (
          <ProvisionedAppMenu
            resource={resource}
            isDetailedViewOn={isDetailedViewOn}
            toggleDetailedView={toggleDetailedView}
          />
        );
      case "status":
        return isHeatDeployment && orchestrationStack ? (
          <ResourceIcon
            status={(orchestrationStack.stack_status || "") as string}
          />
        ) : (
          <ResourceIcon status="n/a" />
        );
      case "id":
        return (
          <QuickView>
            <QuickView.Trigger>{resource.id.toString()}</QuickView.Trigger>
            <QuickView.Content>
              <QuickView.Item>{`Provisioned Deployment ID: ${resource.id}`}</QuickView.Item>
              <QuickView.Copy copy={resource.id.toString()}>
                Copy ID
              </QuickView.Copy>
            </QuickView.Content>
          </QuickView>
        );

      case "resource":
        return isHeatDeployment && orchestrationStack ? (
          <OrchestrationStackQuickView
            stack={orchestrationStack}
            trigger={
              (orchestrationStack.stack_name ||
                resource.appTitle ||
                "") as string
            }
          />
        ) : (
          <>{resource.appTitle || "- None -"}</>
        );

      default:
        return <>{resource[item as keyof ProvisionedAppType] || "- None -"}</>;
    }
  };

  return (
    <>
      {columns.map((col) => (
        <div
          className={`${detailedClassNames} ${paddingJustificationForIcon(
            col,
          )}`}
          key={col.name}
          onClick={toggleDetailedView}
        >
          <Item item={col.name} />
        </div>
      ))}

      {isDetailedViewOn && (
        <div className="grid__detailed-view">
          <div
            className="grid__close-detailed-view"
            onClick={toggleDetailedView}
          />
          <div className="grid__detailed-view-content">
            <MoreData resource={resource} stack={orchestrationStack} />
          </div>
        </div>
      )}
    </>
  );
};

export default CustomizedRow;
